import type {
  MultiLanguageChronicleItem,
  PlatformType,
  GetLocalizedDefaultItemsOptions,
  GetLocalizedItemsOptions,
  GetResult,
  ItemIdentifier,
  Language,
  DeviceOutput,
  LocalizedChronicleItem,
  RemoveInput,
  RequestOptions,
} from "./types";
import * as api from "./chronicle/api";
import { validateOnUpsert } from "./chronicle/validation";
import { cacheItem, resolveItemIdentifier } from "./chronicle/cache";
import { transformLocalizedToMultiLanguageItems } from "./chronicle/transform";

/**
 * Create or update a chronicle item.
 *
 * @public
 */
export async function upsert(
  chronicle_item_id: string,
  item: MultiLanguageChronicleItem,
  /** @internal */
  source?: string,
  requestOptions?: RequestOptions,
): Promise<void> {
  let productSlug: string | null;
  if (source !== "indexeddb") {
    productSlug = Check24.platform.slug.get();
  } else {
    productSlug = item.product_slug || null;
  }

  if (!productSlug) {
    return Promise.reject(new Error("Product slug is required"));
  }

  return Promise.resolve()
    .then(() => validateOnUpsert(item))
    .catch((errors: unknown) => {
      console.error("Invalid Chronicle Item object provided", errors);
      return Promise.reject("Invalid Chronicle Item object provided");
    })
    .then(() =>
      api.putItem(
        {
          chronicleItemId: chronicle_item_id,
          item,
          productSlug,
          source,
        },
        requestOptions,
      ),
    )
    .catch((error: unknown) => {
      if (error instanceof DOMException && error.name === "AbortError") {
        return Promise.reject(error);
      }
      const message = error instanceof Error ? error.message : String(error);
      return Promise.reject(`Upsert error: ${message}`);
    });
}

/**
 * Remove an existing item from the chronicle
 *
 * **This is the legacy function, it will be removed after 2026-08-01.**
 *
 * @public
 * @deprecated Use removeItem instead. This function will be removed after 2026-08-01.
 */
export async function remove(
  chronicle_item_id: RemoveInput,
  requestOptions?: RequestOptions,
): Promise<void> {
  const fallbackProductSlug = Check24.platform.slug.get();
  const itemIdentifier = resolveItemIdentifier(
    chronicle_item_id,
    fallbackProductSlug,
  );
  return api.deleteItem(itemIdentifier, requestOptions);
}

/**
 * Remove an existing item from the chronicle.
 *
 * @public
 */
export async function removeItem(
  itemIdentifier: ItemIdentifier,
  requestOptions?: RequestOptions,
): Promise<void> {
  return api.deleteItem(itemIdentifier, requestOptions);
}

/**
 * Load the users chronicle items.
 * Chronicle items are converted into multi-language format.
 *
 * **This is the legacy function, it will be removed after 2026-08-01.**
 *
 * @public
 * @deprecated Use getLocalizedItems instead. This function will be removed after 2026-08-01.
 */
export async function get(
  includeActivities?: boolean,
  limit?: number,
  cursor?: string,
  language?: Language,
  activitiesDeviceOutput?: DeviceOutput,
  requestOptions?: RequestOptions,
): Promise<GetResult<MultiLanguageChronicleItem>> {
  const emptyResult: GetResult<MultiLanguageChronicleItem> = {
    chronicle_items: [],
    _summary: { activity: 0, chronicle: 0 },
  };

  const resolvedLanguage =
    language || (Check24.platform.language.get() as Language);

  try {
    const localizedResponse = await getLocalizedItems(
      {
        language: resolvedLanguage,
        activitiesDeviceOutput: activitiesDeviceOutput,
        includeActivities: true === includeActivities,
        limit: limit ?? undefined,
        cursor: cursor ?? undefined,
      },
      requestOptions,
    );

    const chronicleItems = await transformLocalizedToMultiLanguageItems(
      localizedResponse.chronicle_items,
      {
        language: resolvedLanguage,
      },
    );

    return {
      chronicle_items: chronicleItems,
      _summary: localizedResponse._summary,
      _pagination: localizedResponse._pagination,
    };
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw error;
    }
    return emptyResult;
  }
}

/**
 * Load the users chronicle items.
 * @public
 */
export async function getLocalizedItems(
  options: GetLocalizedItemsOptions,
  requestOptions?: RequestOptions,
): Promise<GetResult<LocalizedChronicleItem>> {
  return api.getItems(options, requestOptions).then((response) => {
    response.chronicle_items.forEach((item) => {
      cacheItem(item);
    });
    return response;
  });
}

/**
 * Load the users default chronicle items.
 * Default chronicle items are converted into multi-language format.
 *
 * **This is the legacy function, it will be removed after 2026-08-01.**
 *
 * @public
 * @deprecated Use getLocalizedDefaultItems instead. This function will be removed after 2026-08-01.
 */
export async function getDefaultItems(
  language?: string,
  platformType?: PlatformType,
  requestOptions?: RequestOptions,
): Promise<Array<MultiLanguageChronicleItem>> {
  const resolvedLanguage = (language ||
    Check24.platform.language.get()) as Language;

  const localizedItems = await getLocalizedDefaultItems(
    {
      language: resolvedLanguage,
      platformType: platformType || (Check24.platform.isApp() ? "app" : "web"),
    },
    requestOptions,
  );

  return transformLocalizedToMultiLanguageItems(localizedItems, {
    language: resolvedLanguage,
  });
}

/**
 * Load the users default chronicle items.
 *
 * @public
 */
export async function getLocalizedDefaultItems(
  options: GetLocalizedDefaultItemsOptions,
  requestOptions?: RequestOptions,
): Promise<Array<LocalizedChronicleItem>> {
  return api.getDefaultItems(options, requestOptions);
}

export type {
  ChronicleItemBase,
  CountryCode,
  MultiLanguageChronicleItem,
  PlatformType,
  DisplayMode,
  DisplayablePrice,
  Env,
  GetLocalizedDefaultItemsOptions,
  GetLocalizedItemsOptions,
  GetResult,
  HttpsUrlString,
  ItemIdentifier,
  Iso4217String,
  Language,
  DeviceOutput,
  LocalizedChronicleItem,
  LocalizedPricing,
  Money,
  Pagination,
  Rfc3339DateTimeString,
  RemoveInput,
  RequestOptions,
  SourceType,
  StyledText,
  Summary,
  TextStyle,
  TranslatedString,
} from "./types";
